import { log, ProcessingNotification, PushProcessingNotification } from "@repo/helpers";

export function buildSaveChain<T, TIn = any>(actionFn: (input: TIn) => Promise<T>, message?: string) {
	return buildSaveChainInternalAsync<T, TIn, TIn>(actionFn, message, []);
}
export function buildSaveChainInternalAsync<T, TIn, TInOriginal>(
	actionFnAsync: (input: TIn) => Promise<T>,
	message: string,
	actions: SaveAction[],
) {
	return buildSaveChainInternal<T, TIn, TInOriginal>({ actionFnAsync, message }, actions);
}
export function buildSaveChainInternalSync<T, TIn, TInOriginal>(
	actionFn: (input: TIn) => T,
	message: string,
	actions: SaveAction[],
) {
	return buildSaveChainInternal<T, TIn, TInOriginal>({ actionFn, message }, actions);
}
export function buildSaveChainInternal<T, TIn, TInOriginal>(action: SaveAction<TIn, T>, actions: SaveAction[]) {
	actions.push(action);
	return {
		action,
		then,
		thenSync,
		thenRun,
		thenRunSync,
		execute,
	};

	function then<TOut>(actionFn: (input?: T) => Promise<TOut>, message?: string) {
		return buildSaveChainInternalAsync<TOut, T, TInOriginal>(actionFn, message, actions);
	}
	function thenRun(actionFn: (input?: T) => Promise<any>, message?: string) {
		return then((input: T) => actionFn(input).then(() => input), message);
	}
	function thenSync<TOut>(actionFn: (input?: T) => TOut, message?: string) {
		return buildSaveChainInternalSync<TOut, T, TInOriginal>(actionFn, message, actions);
	}
	function thenRunSync(actionFn: (input?: T) => any, message?: string) {
		return thenSync((input: T) => {
			actionFn(input);
			return input;
		}, message);
	}

	async function execute(input?: TInOriginal, notify?: ProcessingNotification) {
		try {
			for (const action of actions) {
				const { message, actionFn, actionFnAsync } = action;
				if (!notify) notify = PushProcessingNotification(message);
				else if (message) notify.processing(message);
				if (actionFn) input = actionFn(input);
				else input = await actionFnAsync(input);
			}
			notify.success("Saved!");
		} catch (ex) {
			notify.error("Error saving");
			log(ex, "error");
			throw ex;
		}
		return input;
	}
}

type SaveAction<TIn = any, TOut = any> = {
	message?: string;
	actionFnAsync?: (input?: TIn) => Promise<TOut>;
	actionFn?: (input?: TIn) => TOut;
};
